import { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatErr } from "@/lib/api";
import { fromISTInput } from "@/lib/format";
import { Field } from "@/components/common";
import DatePicker from "@/components/DatePicker";

function ListEditor({ items, setItems, min, max, prefix }) {
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input data-testid={`${prefix}-${i}`} value={v} onChange={(e) => setItems(items.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Item ${i + 1}`} />
          {items.length > min && <Button type="button" variant="ghost" size="icon" onClick={() => setItems(items.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}
        </div>
      ))}
      {items.length < max && <Button type="button" variant="outline" size="sm" onClick={() => setItems([...items, ""])} data-testid={`${prefix}-add`}><Plus className="mr-1 h-4 w-4" />Add</Button>}
    </div>
  );
}

export function PollDialog({ open, onOpenChange, meetingTag }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState(["", ""]);
  const [multiple, setMultiple] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [deadline, setDeadline] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/chat/polls", { question: q, options: opts, multiple, anonymous, deadline: fromISTInput(deadline), meeting_tag: meetingTag });
      setQ(""); setOpts(["", ""]); setMultiple(false); setAnonymous(false); setDeadline("");
      onOpenChange(false);
    } catch (ex) {
      toast.error(formatErr(ex));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="poll-dialog">
        <DialogHeader><DialogTitle>Create poll</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Question" required><Input data-testid="poll-question-input" value={q} onChange={(e) => setQ(e.target.value)} required /></Field>
          <Field label="Options (2–10)"><ListEditor items={opts} setItems={setOpts} min={2} max={10} prefix="poll-option" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Multiple choice"><Switch checked={multiple} onCheckedChange={setMultiple} data-testid="poll-multiple-switch" /></Field>
            <Field label="Anonymous"><Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="poll-anonymous-switch" /></Field>
          </div>
          <Field label="Deadline (optional, IST)"><DatePicker withTime testid="poll-deadline-input" value={deadline} onChange={setDeadline} /></Field>
          <DialogFooter><Button type="submit" disabled={!q.trim() || opts.filter((o) => o.trim()).length < 2} data-testid="poll-submit-button">Post poll</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ChecklistDialog({ open, onOpenChange, meetingTag }) {
  const [title, setTitle] = useState("");
  const [items, setItems] = useState(["", ""]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/chat/checklists", { title, items, meeting_tag: meetingTag });
      setTitle(""); setItems(["", ""]);
      onOpenChange(false);
    } catch (ex) {
      toast.error(formatErr(ex));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="checklist-dialog">
        <DialogHeader><DialogTitle>Post checklist</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Title" required><Input data-testid="checklist-title-input" value={title} onChange={(e) => setTitle(e.target.value)} required /></Field>
          <Field label="Items"><ListEditor items={items} setItems={setItems} min={1} max={50} prefix="checklist-item" /></Field>
          <DialogFooter><Button type="submit" disabled={!title.trim() || !items.some((i) => i.trim())} data-testid="checklist-submit-button">Post checklist</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
